import { cx } from "./tokens.ts";
import { Tooltip } from "./Tooltip.tsx";

export type CodeBadgeProps = {
  /** Shown as is, monospaced ("F", "#DIV/0!"). */
  text: string;
  /**
   * "letter": a column letter on a table header or a property list (chip background, tertiary text);
   * "error": a spreadsheet error code in danger text (formula cells); "errorFilled": the same on the danger
   * background (the formula editor's preview line); "ref": a formula reference in its `formulaRef.N` colour (the
   * Sheet formula bar's chips, matching the dashed ranges on the grid).
   */
  variant: "letter" | "error" | "errorFilled" | "ref";
  /** "ref": which formulaRef colour (1…6). */
  refColor?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Hover / keyboard-focus explanation (an error's message). The badge becomes focusable. */
  tooltip?: string;
  testID?: string;
};

/**
 * A short monospaced code: column letters ("Show row numbers") and formula error codes. Native: a Text in the mono
 * font with the same tokens; the tooltip is `.help` on iOS and a long press on phones.
 */
export function CodeBadge({ text, variant, refColor, tooltip, testID }: CodeBadgeProps) {
  const badge = (
    <span
      className={cx("b-code-badge", `b-code-badge-${variant}`, variant === "ref" && `b-code-badge-ref-${refColor ?? 1}`)}
      tabIndex={tooltip === undefined ? undefined : 0}
      aria-label={tooltip === undefined ? undefined : `${text}: ${tooltip}`}
      data-testid={testID}
    >
      {text}
    </span>
  );
  return tooltip === undefined ? badge : <Tooltip label={tooltip}>{badge}</Tooltip>;
}
